import {AnimatePresence, animate, motion, useMotionValue} from "framer-motion"
import {FC, KeyboardEvent, useCallback, useRef, useState} from "react"

import {WIDGET_HEIGHT_MEDIA_QUERIES} from "@frontend/widget/styles"
import {ConfirmedCompatiblePart} from "@ri2/db/client"
import {css} from "@styled-system/css"

import {CompatiblePartCard} from "./CompatiblePartCard"

interface Props {
  parts: ConfirmedCompatiblePart[]
  caseId: string
  modelNumber: string | null
  compact?: boolean
}

/**
 * Horizontal snap-scroll carousel for displaying multiple part cards.
 * Uses framer-motion for smooth spring-based transitions and drag-to-swipe.
 *
 * UX rules:
 *  - In compact/short-viewport contexts, dot indicators render ABOVE the
 *    track so pagination is visible even when the card uses the full width.
 *  - Arrows hide on touch devices (swipe is the native gesture there) and
 *    are sized to meet the 36px tap-target minimum on desktop.
 *  - Supports keyboard navigation via Left/Right arrow keys.
 */
export const PartsCarousel: FC<Props> = ({
  parts,
  caseId,
  modelNumber,
  compact = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const x = useMotionValue(0)

  const GAP = 8
  // Tighter peek in compact/widget contexts — a 15% sibling bleed looks
  // glitchy in a 380px widget; 8% reads as a clean hint.
  const CARD_RATIO = compact ? 0.92 : 0.85
  const CARD_WIDTH_PCT = `${CARD_RATIO * 100}%`

  /** Returns card width (without gap) and the centering inset. */
  const getLayout = useCallback(() => {
    const container = containerRef.current
    if (!container) return {cardWidth: 280, inset: 0}
    const containerWidth = container.offsetWidth
    const cardWidth = Math.min(containerWidth * CARD_RATIO, 340)
    const inset = (containerWidth - cardWidth) / 2
    return {cardWidth, inset}
  }, [CARD_RATIO])

  /** Compute the x offset that centers the card at `index`,
   *  but keeps the first card flush-left and the last card flush-right. */
  const getOffsetForIndex = useCallback(
    (index: number) => {
      const {cardWidth, inset} = getLayout()
      const centered = -index * (cardWidth + GAP) + inset
      const totalTrackWidth =
        parts.length * cardWidth + (parts.length - 1) * GAP
      const containerWidth = containerRef.current?.offsetWidth ?? 0
      const minOffset = -(totalTrackWidth - containerWidth)
      return Math.max(minOffset, Math.min(centered, 0))
    },
    [getLayout, parts.length],
  )

  const scrollToIndex = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(index, parts.length - 1))
      setActiveIndex(clamped)
      animate(x, getOffsetForIndex(clamped), {
        type: "spring",
        stiffness: 400,
        damping: 40,
      })
    },
    [parts.length, getOffsetForIndex, x],
  )

  const handleDragEnd = useCallback(
    (
      _: MouseEvent | TouchEvent | PointerEvent,
      info: {offset: {x: number}; velocity: {x: number}},
    ) => {
      const {cardWidth, inset} = getLayout()
      const stride = cardWidth + GAP

      // Project where the track will land, factoring in the user's release
      // velocity. This lets a long drag land multiple cards over, and a
      // short-but-fast flick carry momentum — matching native iOS/Android feel.
      const VELOCITY_PROJECTION = 0.2
      const currentX = x.get()
      const projectedX = currentX + info.velocity.x * VELOCITY_PROJECTION

      // Convert track position to a card index.
      //   trackX = -index * stride + inset  →  index = (inset - trackX) / stride
      const rawIndex = (inset - projectedX) / stride
      let newIndex = Math.round(rawIndex)

      // Safety: if the user clearly flicked but the projection didn't push us
      // past the half-card line (e.g. starting position was exactly centered),
      // force a ±1 step in the flick direction.
      const FLICK_VELOCITY = 400
      if (newIndex === activeIndex) {
        if (info.velocity.x < -FLICK_VELOCITY) newIndex = activeIndex + 1
        else if (info.velocity.x > FLICK_VELOCITY) newIndex = activeIndex - 1
      }

      scrollToIndex(newIndex)
    },
    [activeIndex, getLayout, x, scrollToIndex],
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "ArrowRight") {
        e.preventDefault()
        scrollToIndex(activeIndex + 1)
      } else if (e.key === "ArrowLeft") {
        e.preventDefault()
        scrollToIndex(activeIndex - 1)
      }
    },
    [activeIndex, scrollToIndex],
  )

  const canGoPrev = activeIndex > 0
  const canGoNext = activeIndex < parts.length - 1

  return (
    <div
      ref={containerRef}
      role="region"
      aria-roledescription="carousel"
      aria-label="Compatible parts"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className={css({
        width: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        _focusVisible: {
          outline: "2px solid token(colors.background.brand.accent)",
          outlineOffset: "2px",
          borderRadius: "4px",
        },
      })}
    >
      {/* Dot indicators — rendered first in DOM, visually flipped to below
          via `order` in the default (hero) layout. */}
      {parts.length > 1 && (
        <div
          aria-live="polite"
          aria-atomic="true"
          className={css({
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "6px",
            // Below the track by default.
            order: 2,
            marginTop: "8px",
            // Above the track when compact or on short viewports — keeps
            // pagination visible regardless of card height.
            ...(compact ? {order: 0, marginTop: 0, marginBottom: "8px"} : {}),
            [WIDGET_HEIGHT_MEDIA_QUERIES.compact]: {
              order: 0,
              marginTop: 0,
              marginBottom: "8px",
            },
          })}
        >
          <span
            className={css({
              fontSize: 11,
              color: "text.tertiary",
              marginRight: "6px",
              fontVariantNumeric: "tabular-nums",
            })}
          >
            {activeIndex + 1}
            {" / "}
            {parts.length}
          </span>
          {parts.map((part, i) => (
            <motion.button
              key={part.id}
              onClick={() => scrollToIndex(i)}
              aria-label={`Go to part ${i + 1} of ${parts.length}`}
              aria-current={i === activeIndex ? "true" : undefined}
              animate={{
                backgroundColor:
                  i === activeIndex
                    ? "var(--colors-text-primary)"
                    : "var(--colors-border-primary-300)",
                scale: i === activeIndex ? 1.3 : 1,
              }}
              transition={{type: "spring", stiffness: 500, damping: 30}}
              className={css({
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                border: "none",
                padding: 0,
                cursor: "pointer",
              })}
            />
          ))}
        </div>
      )}

      {/* Track wrapper — arrows position relative to this, not the whole
          region, so they stay centered on the card even when dots flip above. */}
      <div
        className={css({
          position: "relative",
          overflow: "hidden",
          order: 1,
        })}
      >
        <motion.div
          style={{x, display: "flex", gap: 8, alignItems: "stretch"}}
          drag="x"
          dragConstraints={{
            left: getOffsetForIndex(parts.length - 1),
            right: getOffsetForIndex(0),
          }}
          dragElastic={0.08}
          // Disable framer-motion's built-in momentum animation — it would
          // fight our spring-based snap below and cause the "skip" feel.
          dragMomentum={false}
          onDragEnd={handleDragEnd}
          className={css({cursor: "grab", _active: {cursor: "grabbing"}})}
        >
          {parts.map((part, i) => (
            <motion.div
              key={part.id}
              aria-roledescription="slide"
              aria-label={`Part ${i + 1} of ${parts.length}`}
              aria-hidden={i !== activeIndex ? "true" : undefined}
              style={{width: `min(${CARD_WIDTH_PCT}, 340px)`}}
              className={css({
                flexShrink: 0,
                "& > div": {
                  maxWidth: "none",
                  width: "100%",
                },
              })}
            >
              <CompatiblePartCard
                part={part}
                caseId={caseId}
                modelNumber={modelNumber}
                compact={compact}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Navigation arrows. Hidden on touch devices (swipe is native). */}
        <AnimatePresence>
          {canGoPrev && (
            <motion.button
              key="prev-arrow"
              initial={{opacity: 0, scale: 0.8}}
              animate={{opacity: 1, scale: 1}}
              exit={{opacity: 0, scale: 0.8}}
              transition={{duration: 0.15}}
              onClick={() => scrollToIndex(activeIndex - 1)}
              aria-label="Previous part"
              className={css({
                position: "absolute",
                left: "6px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                border: "1px solid token(colors.border.primary.300)",
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                zIndex: 2,
                color: "text.primary",
                _hover: {
                  backgroundColor: "background.subtle.primary",
                  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.2)",
                },
                "@media (pointer: coarse)": {
                  display: "none",
                },
              })}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </motion.button>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {canGoNext && (
            <motion.button
              key="next-arrow"
              initial={{opacity: 0, scale: 0.8}}
              animate={{opacity: 1, scale: 1}}
              exit={{opacity: 0, scale: 0.8}}
              transition={{duration: 0.15}}
              onClick={() => scrollToIndex(activeIndex + 1)}
              aria-label="Next part"
              className={css({
                position: "absolute",
                right: "6px",
                top: "50%",
                transform: "translateY(-50%)",
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                border: "1px solid token(colors.border.primary.300)",
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                zIndex: 2,
                color: "text.primary",
                _hover: {
                  backgroundColor: "background.subtle.primary",
                  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.2)",
                },
                "@media (pointer: coarse)": {
                  display: "none",
                },
              })}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 6 15 12 9 18" />
              </svg>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
